import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAPI } from '../../services/api';
import { Ico, Icons, PlayerAvatar } from './index';

export default function GlobalSearch({ open, onClose }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const timer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) { setQ(''); setResults(null); setTimeout(() => inputRef.current?.focus(), 100); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const doSearch = useCallback(async (query) => {
    if (query.length < 2) { setResults(null); return; }
    setLoading(true);
    try {
      const res = await searchAPI.search(query);
      setResults(res.data);
    } catch { setResults(null); }
    setLoading(false);
  }, []);

  const onChange = (val) => {
    setQ(val);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => doSearch(val), 300);
  };

  const go = (path) => { onClose(); navigate(path); };

  if (!open) return null;

  const hasResults = results && (results.players?.length || results.tournaments?.length || results.matches?.length);
  const noResults = results && !hasResults && q.length >= 2;

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.container} onClick={e => e.stopPropagation()}>
        <div style={S.inputRow}>
          <Ico d={Icons.eye} size={18} color="#8892A6" />
          <input ref={inputRef} value={q} onChange={e => onChange(e.target.value)} placeholder="Search players, tournaments, matches..." style={S.input} autoFocus />
          {q && <button onClick={() => { setQ(''); setResults(null); inputRef.current?.focus(); }} style={S.clearBtn}>✕</button>}
          <button onClick={onClose} style={S.escBtn}>ESC</button>
        </div>

        <div style={S.body}>
          {loading && <div style={S.msg}>Searching...</div>}
          {!loading && noResults && <div style={S.msg}>No results for "{q}"</div>}
          {!loading && !results && q.length < 2 && <div style={S.msg}>Type at least 2 characters</div>}

          {hasResults && (
            <>
              {results.players?.length > 0 && (
                <div>
                  <div style={S.sectionTitle}><Ico d={Icons.user} size={12} color="#8892A6" /> Players</div>
                  {results.players.map(p => (
                    <div key={p._id || p.id} style={S.item} onClick={() => go(`/players/${p._id || p.id}`)}>
                      <PlayerAvatar name={p.name} photo={p.photo} size={28} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={S.itemTitle}>{p.name}</div>
                        <div style={S.itemSub}>{p.wins || 0}W · {p.losses || 0}L{p.titles ? ` · ${p.titles} 🏆` : ''}</div>
                      </div>
                      <Ico d={Icons.chevron} size={14} color="#B0B8C9" />
                    </div>
                  ))}
                </div>
              )}

              {results.tournaments?.length > 0 && (
                <div>
                  <div style={S.sectionTitle}><Ico d={Icons.trophy} size={12} color="#8892A6" /> Tournaments</div>
                  {results.tournaments.map(t => (
                    <div key={t._id || t.id} style={S.item} onClick={() => go(`/tournaments/${t._id || t.id}`)}>
                      <div style={S.tourneyIcon}><Ico d={Icons.trophy} size={14} color="#00205B" /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={S.itemTitle}>{t.name}</div>
                        <div style={S.itemSub}>
                          {t.type}{t.surface ? ` · ${t.surface}` : ''}{t.location ? ` · ${t.location}` : ''}
                          {t.status && <span style={{ ...S.badge, background: t.status === 'ongoing' ? '#FFEBEE' : t.status === 'completed' ? '#E8F5E9' : '#EEF1F6', color: t.status === 'ongoing' ? '#E53935' : t.status === 'completed' ? '#43A047' : '#5F6B80' }}>{t.status}</span>}
                        </div>
                      </div>
                      <Ico d={Icons.chevron} size={14} color="#B0B8C9" />
                    </div>
                  ))}
                </div>
              )}

              {results.matches?.length > 0 && (
                <div>
                  <div style={S.sectionTitle}><Ico d={Icons.racket} size={12} color="#8892A6" /> Matches</div>
                  {results.matches.map(m => (
                    <div key={m._id || m.id} style={S.item} onClick={() => go(`/matches/${m._id || m.id}`)}>
                      <div style={{ ...S.tourneyIcon, background: m.status === 'live' ? '#FFEBEE' : m.status === 'completed' ? '#E8F5E9' : '#EEF1F6' }}>
                        <Ico d={Icons.racket} size={14} color={m.status === 'live' ? '#E53935' : m.status === 'completed' ? '#43A047' : '#5F6B80'} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={S.itemTitle}>{m.player1Name || 'TBD'} vs {m.player2Name || 'TBD'}</div>
                        <div style={S.itemSub}>
                          {m.round}{m.tournamentName ? ` · ${m.tournamentName}` : ''}
                          <span style={{ ...S.badge, background: m.status === 'live' ? '#FFEBEE' : m.status === 'completed' ? '#E8F5E9' : '#EEF1F6', color: m.status === 'live' ? '#E53935' : m.status === 'completed' ? '#43A047' : '#5F6B80' }}>{m.status}</span>
                        </div>
                      </div>
                      <Ico d={Icons.chevron} size={14} color="#B0B8C9" />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'center', paddingTop: 60, animation: 'fadeIn 0.15s ease' },
  container: { width: '100%', maxWidth: 520, maxHeight: 'calc(100vh - 120px)', background: 'var(--card-bg)', borderRadius: 16, border: '1px solid var(--card-border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', overflow: 'hidden', margin: '0 12px' },
  inputRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--card-border)' },
  input: { flex: 1, border: '1px solid rgba(0,32,91,0.3)', outline: 'none', fontSize: 15, fontWeight: 500, color: 'var(--text-primary)', background: 'transparent', fontFamily: "'Outfit', sans-serif", borderRadius: 8, padding: '8px 12px' },
  clearBtn: { background: 'none', border: 'none', color: 'var(--gray-300)', cursor: 'pointer', fontSize: 14, padding: 4 },
  escBtn: { fontSize: 9, fontWeight: 700, color: 'var(--gray-400)', background: 'var(--gray-100)', padding: '3px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', letterSpacing: 0.5 },
  body: { flex: 1, overflowY: 'auto', padding: '8px 0' },
  msg: { textAlign: 'center', padding: '32px 16px', fontSize: 13, color: 'var(--gray-400)', fontFamily: "'Outfit', sans-serif" },
  sectionTitle: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', letterSpacing: 1, padding: '10px 16px 4px', fontFamily: "'Outfit', sans-serif", textTransform: 'uppercase' },
  item: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', cursor: 'pointer', transition: 'background 0.15s' },
  itemTitle: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  itemSub: { fontSize: 11, color: 'var(--gray-400)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  badge: { fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, textTransform: 'capitalize' },
  tourneyIcon: { width: 28, height: 28, borderRadius: 8, background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
};
